import React, { useState, useEffect } from 'react';
import products, { saveProducts, generateId, initializeProducts } from '../data/products';
import { useToast } from '../context/ToastContext';
import ProductImage from '../components/ProductImage';
import { 
  loginUser, 
  logoutUser, 
  fetchProducts, 
  fetchOrders, 
  createProduct, 
  updateProduct, 
  deleteProduct,
  updateOrderStatus as apiUpdateOrderStatus,
  fetchInventory,
  fetchLowStockProducts,
  updateInventory,
  restockProduct
} from '../services/api';
import './Admin.css';

function Admin() {
  // Toast notifications
  const { showSuccess, showError, showInfo, showWarning } = useToast();
  
  const [activeTab, setActiveTab] = useState('orders');
  const [productCatalog, setProductCatalog] = useState({...products});
  const [selectedCategory, setSelectedCategory] = useState('smoothies');
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    price: 0,
    category: 'smoothies',
    image: '',
    ingredients: [],
    nutritionFacts: {
      calories: 0,
      protein: 0,
      fat: 0,
      carbs: 0
    },
    inventory: {
      inStock: true,
      stockQuantity: 0,
      lowStockThreshold: 5,
      sku: '',
      lastRestocked: new Date().toISOString()
    }
  });
  
  // State for image file upload
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // State for orders that will be fetched from API
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for inventory management
  const [inventoryData, setInventoryData] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [restockData, setRestockData] = useState({
    productId: '',
    quantity: 0,
    name: ''
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if token exists - use new token name
    return !!localStorage.getItem('token');
  });
  const [username, setUsername] = useState(() => {
    // Try to get remembered user
    const rememberedUser = localStorage.getItem('adminUser');
    if (rememberedUser) {
      try {
        const userData = JSON.parse(rememberedUser);
        return userData.email || '';
      } catch (error) {
        return '';
      }
    }
    return '';
  });
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Default to true
  const [loginError, setLoginError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Load products, orders, and inventory when component mounts
  useEffect(() => {
    // Only fetch data if user is authenticated
    if (isAuthenticated) {
      // Load products from API
      setIsLoading(true);
      
      const loadData = async () => {
        try {
          // Load products
          const productsData = await initializeProducts();
          setProductCatalog(productsData);
          
          // Load orders
          const ordersData = await fetchOrders();
          setOrders(ordersData);
          
          // Load inventory data
          const inventoryItems = await fetchInventory();
          setInventoryData(inventoryItems);
          
          // Load low stock products
          const lowStockProducts = await fetchLowStockProducts();
          setLowStockItems(lowStockProducts);
          
          setError(null);
        } catch (err) {
          console.error('Error loading data:', err);
          setError('Failed to load data. Please try again.');
          
          // If authentication error, log out
          if (err.message === 'Authentication token expired') {
            setIsAuthenticated(false);
          }
        } finally {
          setIsLoading(false);
        }
      };
      
      loadData();
    }
  }, [isAuthenticated]);
  
  // Product management functions
  const handleAddProduct = () => {
    // Set to null to indicate we're adding a new product rather than editing
    setEditingProduct(null);
    
    // Generate a unique SKU for new product
    const sku = `SKU-${Date.now().toString().slice(-6)}`;
    
    // Initialize form with default values - use empty name instead of space
    setFormData({
      id: '',
      name: '', // Empty string - no leading space that could cause validation issues
      description: '',
      price: 0,
      category: selectedCategory,
      image: '',
      ingredients: [],
      nutritionFacts: {
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0
      },
      inventory: {
        inStock: true,
        stockQuantity: 0,
        lowStockThreshold: 5,
        sku: sku,
        lastRestocked: new Date().toISOString()
      }
    });
    
    // Scroll to the form
    setTimeout(() => {
      const formContainer = document.querySelector('.product-form-container');
      if (formContainer) {
        formContainer.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  const handleEditProduct = (product) => {
    console.log('Editing product:', product);
    setEditingProduct(product);
    
    // Make deep copy of product and handle both id formats
    const formattedProduct = {
      ...product,
      // Ensure both id formats are available
      id: product.id || product._id,
      _id: product._id || product.id,
      // Make deep copies of arrays and objects
      ingredients: [...product.ingredients],
      nutritionFacts: {...product.nutritionFacts}
    };
    
    setFormData(formattedProduct);
    
    // Reset the image file and preview to use server image if available
    setImageFile(null);
    setImagePreview('');
    
    // Reset file input if exists
    const fileInput = document.getElementById('image-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
    
    // Switch to products tab where the edit form is displayed
    setActiveTab('products');
    
    // Scroll to the form after tab switch (with a slight delay to ensure rendering)
    setTimeout(() => {
      const formContainer = document.querySelector('.product-form-container');
      if (formContainer) {
        formContainer.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  const handleDeleteProduct = async (productId) => {
    const confirmed = window.confirm('Are you sure you want to delete this product?');
    if (!confirmed) return;
    
    try {
      // Find the product to delete to get both id and _id if available
      const productToDelete = productCatalog[selectedCategory].find(
        p => p.id === productId || p._id === productId
      );
      
      if (!productToDelete) {
        throw new Error(`Product with ID ${productId} not found`);
      }
      
      console.log('Deleting product:', productToDelete);
      
      // Delete from API first
      await deleteProduct(productToDelete._id || productToDelete.id);
      
      // If successful, update local state
      const updatedCatalog = {...productCatalog};
      updatedCatalog[selectedCategory] = updatedCatalog[selectedCategory].filter(
        p => p.id !== productId && p._id !== productId
      );
      
      setProductCatalog(updatedCatalog);
      saveProducts(updatedCatalog);
      
      // Show success message
      showSuccess('Product deleted successfully!');
    } catch (error) {
      console.error('Failed to delete product:', error);
      showError(`Failed to delete product: ${error.message || 'Please try again.'}`);
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    
    // Handle file upload
    if (name === 'imageFile' && files && files.length > 0) {
      const file = files[0];
      
      // Check if it's an image file
      if (!file.type.startsWith('image/')) {
        showError('Please select an image file (jpg, png, etc.)');
        return;
      }
      
      // Check file size - limit to 5MB
      if (file.size > 5 * 1024 * 1024) {
        showError('Image file is too large. Please select an image smaller than 5MB.');
        e.target.value = '';
        return;
      }
      
      console.log('Image file selected:', file.name, file.type, `${file.size} bytes`);
      
      // Store the file in state
      setImageFile(file);
      
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      
      return;
    }
    
    if (name.includes('nutritionFacts.')) {
      const nutritionField = name.split('.')[1];
      setFormData({
        ...formData,
        nutritionFacts: {
          ...formData.nutritionFacts,
          [nutritionField]: parseFloat(value) || 0
        }
      });
    } else if (name.includes('inventory.')) {
      const inventoryField = name.split('.')[1];
      setFormData({
        ...formData,
        inventory: {
          ...formData.inventory,
          [inventoryField]: inventoryField === 'stockQuantity' || inventoryField === 'lowStockThreshold' 
            ? (parseInt(value) || 0) 
            : value
        }
      });
    } else if (name === 'ingredients') {
      setFormData({
        ...formData,
        ingredients: value.split(',').map(item => item.trim())
      });
    } else if (name === 'price') {
      setFormData({
        ...formData,
        price: parseFloat(value) || 0
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  const handleSaveProduct = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.name || formData.name.trim() === '') {
      showError('Product name is required');
      return;
    }
    
    // Validate image is provided (either a new file upload or existing image)
    if (!imageFile && !formData.image) {
      showError('Product image is required');
      return;
    }
    
    // Log form data for debugging
    console.log('Form data being submitted:', JSON.stringify(formData));
    if (imageFile) {
      console.log('Image file size:', imageFile.size, 'bytes');
      console.log('Image file type:', imageFile.type);
    }
    
    // Create a sanitized copy of formData with trimmed strings
    const sanitizedFormData = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
      image: formData.image.trim(),
      // Create new arrays/objects to avoid reference issues
      ingredients: formData.ingredients.map(ing => typeof ing === 'string' ? ing.trim() : ing),
      nutritionFacts: { ...formData.nutritionFacts },
      inventory: { ...formData.inventory }
    };
    
    try {
      const updatedCatalog = {...productCatalog};
      const category = sanitizedFormData.category;

      // Set loading state
      setIsLoading(true);
      
      if (editingProduct) {
        // Update existing product via API
        // Use _id if available, otherwise use id
        const productId = editingProduct._id || sanitizedFormData._id || sanitizedFormData.id;
        
        // Log the update attempt
        console.log(`Updating product with ID: ${productId}`);
        console.log('Product data being sent:', sanitizedFormData);
        
        // Perform the update with image file if available
        const updatedProduct = await updateProduct(productId, sanitizedFormData, imageFile);
        
        console.log('Received updated product from API:', updatedProduct);
        
        // Convert API format to frontend format if needed
        const frontendProduct = {
          ...updatedProduct,
          // Ensure we have a frontend id
          id: updatedProduct.id || updatedProduct._id
        };
        
        // Update local state
        updatedCatalog[category] = updatedCatalog[category].map(
          p => p.id === frontendProduct.id ? frontendProduct : p
        );
      } else {
        // Add new product via API
        const newProduct = {
          ...sanitizedFormData,
          // Only generate id if needed
          id: sanitizedFormData.id || generateId(category)
        };
        
        console.log('Creating new product:', newProduct);
        
        // Send to API with image file if available
        const createdProduct = await createProduct(newProduct, imageFile);
        
        console.log('Received created product from API:', createdProduct);
        
        // Convert API format to frontend format
        const frontendProduct = {
          ...createdProduct,
          // Ensure we have a frontend id
          id: createdProduct.id || createdProduct._id
        };
        
        // Add to local catalog
        updatedCatalog[category] = [...updatedCatalog[category], frontendProduct];
      }
      
      setProductCatalog(updatedCatalog);
      saveProducts(updatedCatalog);
      
      // Reset form
      setEditingProduct(null);
      setFormData({
        id: '',
        name: '',
        description: '',
        price: 0,
        category: selectedCategory,
        image: '',
        ingredients: [],
        nutritionFacts: {
          calories: 0,
          protein: 0,
          fat: 0,
          carbs: 0
        },
        inventory: {
          inStock: true,
          stockQuantity: 0,
          lowStockThreshold: 5,
          sku: `SKU-${Date.now().toString().slice(-6)}`,
          lastRestocked: new Date().toISOString()
        }
      });
      
      // Clear image states
      setImageFile(null);
      setImagePreview('');
      
      // Reset file input if exists
      const fileInput = document.getElementById('image-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Clear loading state
      setIsLoading(false);
      
      // Show success message
      showSuccess('Product saved successfully!');
    } catch (error) {
      console.error('Failed to save product:', error);
      showError(`Failed to save product: ${error.message || 'Please try again.'}`);
      
      // Make sure to clear loading state even on error
      setIsLoading(false);
    }
  };
  
  const handleCancelEdit = () => {
    setEditingProduct(null);
    // Clear the form completely
    setFormData({
      id: '',
      name: '',
      description: '',
      price: 0,
      category: selectedCategory,
      image: '',
      ingredients: [],
      nutritionFacts: {
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0
      },
      inventory: {
        inStock: true,
        stockQuantity: 0,
        lowStockThreshold: 5,
        sku: `SKU-${Date.now().toString().slice(-6)}`,
        lastRestocked: new Date().toISOString()
      }
    });
    
    // Clear image states
    setImageFile(null);
    setImagePreview('');
    
    // Reset file input if exists
    const fileInput = document.getElementById('image-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Call backend API for login with rememberMe flag
      await loginUser({ email: username, password }, rememberMe);
      
      // If successful (no exceptions thrown)
      setIsAuthenticated(true);
      setLoginError('');
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(error.message || 'Invalid credentials');
      showError('Login failed: ' + (error.message || 'Invalid credentials'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    // Show loading indicator
    setIsLoading(true);
    
    try {
      // Call API to log out and invalidate token on server
      await logoutUser(!rememberMe);
      
      // Update local state
      setIsAuthenticated(false);
      setPassword('');
      
      // Only clear username if not remembering
      if (!rememberMe) {
        setUsername('');
      }
      
      // Redirect to login screen (happens automatically due to state change)
      console.log('Successfully logged out');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still mark as logged out even if server error
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Update status in API
      await apiUpdateOrderStatus(orderId, newStatus);
      
      // If successful, update local state
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatus } 
            : order
        )
      );
    } catch (error) {
      console.error(`Failed to update order status for ${orderId}:`, error);
      showError('Failed to update order status. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === filterStatus);
    
  // Show loading spinner if data is being loaded
  if (isLoading && isAuthenticated) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }
  
  // Show error message if there was an error loading data
  if (error && isAuthenticated) {
    return (
      <div className="admin-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={handleLogout} className="logout-button">Logout</button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h1>Admin Login</h1>
          {loginError && <div className="login-error">{loginError}</div>}
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="username">Email</label>
              <input 
                type="email" 
                id="username" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                required 
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input 
                type="password" 
                id="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
                disabled={isLoading}
              />
            </div>
            <div className="remember-me-container">
              <label className="remember-me-label">
                <input 
                  type="checkbox" 
                  checked={rememberMe} 
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                  className="remember-me-checkbox"
                />
                <span>Remember me</span>
              </label>
            </div>
            <button 
              type="submit" 
              className="login-button" 
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <div className="login-hint">
            <p>Connect to your backend API at {process.env.REACT_APP_API_URL || 'http://localhost:5002'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button onClick={handleLogout} className="logout-button">Logout</button>
      </div>
      
      <div className="admin-tabs">
        <button 
          className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Orders
        </button>
        <button 
          className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button 
          className={`tab-button ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          Inventory
          {lowStockItems.length > 0 && (
            <span className="low-stock-badge">{lowStockItems.length}</span>
          )}
        </button>
      </div>

      {activeTab === 'orders' && (
        <>
          <div className="admin-controls">
            <div className="status-filter">
              <label htmlFor="status-select">Filter by status:</label>
              <select 
                id="status-select" 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Orders</option>
                <option value="pending">Pending</option>
                <option value="preparing">Preparing</option>
                <option value="out-for-delivery">Out for Delivery</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="orders-list">
            <h2>
              {filterStatus === 'all' ? 'All Orders' : `${filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Orders`}
            </h2>
            
            {filteredOrders.length === 0 ? (
              <div className="no-orders">No orders found</div>
            ) : (
              filteredOrders.map(order => (
                <div className={`order-card ${order.status}`} key={order.id}>
                  <div className="order-header">
                    <div>
                      <h3>Order #{order.id}</h3>
                      <p className="order-date">{formatDate(order.orderDate)}</p>
                    </div>
                    <div className="order-status">
                      <span className={`status-badge ${order.status}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="order-details">
                    <div className="customer-info">
                      <h4>Customer Information</h4>
                      <p><strong>Name:</strong> {order.customer.name}</p>
                      <p><strong>Phone:</strong> {order.customer.phone}</p>
                      <p><strong>Delivery to:</strong> {order.deliveryAddress}</p>
                      <p><strong>Payment Method:</strong> {order.paymentMethod}</p>
                      <p className="payment-note">Coming soon: Wave Mobile and Orange Money integration</p>
                    </div>
                    
                    <div className="order-items">
                      <h4>Order Items</h4>
                      <table>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((item, index) => (
                            <tr key={index}>
                              <td>{item.product.name}</td>
                              <td>{item.quantity}</td>
                              <td>${item.product.price.toFixed(2)}</td>
                              <td>${(item.product.price * item.quantity).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="3"><strong>Total</strong></td>
                            <td><strong>${order.total}</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                  
                  <div className="order-actions">
                    <h4>Update Status</h4>
                    <div className="status-buttons">
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'pending')}
                        className={`status-btn pending ${order.status === 'pending' ? 'active' : ''}`}
                        disabled={order.status === 'pending'}
                      >
                        Pending
                      </button>
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'preparing')}
                        className={`status-btn preparing ${order.status === 'preparing' ? 'active' : ''}`}
                        disabled={order.status === 'preparing'}
                      >
                        Preparing
                      </button>
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'out-for-delivery')}
                        className={`status-btn out-for-delivery ${order.status === 'out-for-delivery' ? 'active' : ''}`}
                        disabled={order.status === 'out-for-delivery'}
                      >
                        Out for Delivery
                      </button>
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'delivered')}
                        className={`status-btn delivered ${order.status === 'delivered' ? 'active' : ''}`}
                        disabled={order.status === 'delivered'}
                      >
                        Delivered
                      </button>
                      <button 
                        onClick={() => updateOrderStatus(order.id, 'cancelled')}
                        className={`status-btn cancelled ${order.status === 'cancelled' ? 'active' : ''}`}
                        disabled={order.status === 'cancelled'}
                      >
                        Cancelled
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {activeTab === 'inventory' && (
        <div className="inventory-management">
          <div className="inventory-header">
            <h2>Inventory Management</h2>
            {lowStockItems.length > 0 && (
              <div className="low-stock-alert">
                <span className="alert-icon">⚠️</span>
                <span className="alert-text">{lowStockItems.length} products below stock threshold</span>
              </div>
            )}
          </div>
          
          <div className="inventory-tabs">
            <button 
              className={`inventory-tab ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              All Products
            </button>
            <button 
              className={`inventory-tab ${selectedCategory === 'low-stock' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('low-stock')}
            >
              Low Stock
            </button>
          </div>
          
          <div className="inventory-list">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product Name</th>
                  <th>SKU</th>
                  <th>In Stock</th>
                  <th>Quantity</th>
                  <th>Low Threshold</th>
                  <th>Last Restocked</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedCategory === 'all' 
                  ? (productCatalog ? [
                      ...(productCatalog.smoothies || []), 
                      ...(productCatalog.juices || []), 
                      ...(productCatalog.proteinBalls || [])
                    ] : [])
                      .filter(product => product && product.inventory)
                      .map(product => (
                        <tr key={product.id} className={
                          !product.inventory.inStock ? 'out-of-stock' : 
                          product.inventory.stockQuantity <= product.inventory.lowStockThreshold ? 'low-stock' : ''
                        }>
                          <td>{product.id}</td>
                          <td>{product.name}</td>
                          <td>{product.inventory.sku}</td>
                          <td>{product.inventory.inStock ? 'Yes' : 'No'}</td>
                          <td>{product.inventory.stockQuantity}</td>
                          <td>{product.inventory.lowStockThreshold}</td>
                          <td>{new Date(product.inventory.lastRestocked).toLocaleDateString()}</td>
                          <td>
                            <button 
                              className="restock-btn"
                              onClick={() => {
                                setRestockData({
                                  productId: product.id,
                                  quantity: 10
                                });
                                
                                // Show restock modal
                                const modal = document.getElementById('restock-modal');
                                if (modal) {
                                  modal.style.display = 'flex';
                                }
                              }}
                            >
                              Restock
                            </button>
                          </td>
                        </tr>
                      ))
                  : lowStockItems.map(product => (
                      <tr key={product.id} className="low-stock">
                        <td>{product.id}</td>
                        <td>{product.name}</td>
                        <td>{product.inventory.sku}</td>
                        <td>{product.inventory.inStock ? 'Yes' : 'No'}</td>
                        <td>{product.inventory.stockQuantity}</td>
                        <td>{product.inventory.lowStockThreshold}</td>
                        <td>{new Date(product.inventory.lastRestocked).toLocaleDateString()}</td>
                        <td>
                          <button 
                            className="restock-btn"
                            onClick={() => {
                              setRestockData({
                                productId: product._id || product.id,
                                quantity: product.inventory.lowStockThreshold * 2 - product.inventory.stockQuantity,
                                name: product.name // Store name for better error messages
                              });
                              
                              // Show restock modal
                              const modal = document.getElementById('restock-modal');
                              if (modal) {
                                modal.style.display = 'flex';
                              }
                            }}
                          >
                            Restock
                          </button>
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
          
          {/* Restock Modal */}
          <div id="restock-modal" className="modal">
            <div className="modal-content">
              <span 
                className="close-btn" 
                onClick={() => {
                  const modal = document.getElementById('restock-modal');
                  if (modal) {
                    modal.style.display = 'none';
                  }
                }}
              >
                &times;
              </span>
              <h3>Restock Product</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                
                try {
                  setIsLoading(true);
                  
                  // Find the product being restocked
                  const allProducts = productCatalog ? [
                    ...(productCatalog.smoothies || []),
                    ...(productCatalog.juices || []),
                    ...(productCatalog.proteinBalls || [])
                  ] : [];
                  
                  const productToRestock = allProducts.find(p => 
                    p.id === restockData.productId || p._id === restockData.productId
                  );
                  
                  console.log('Attempting to restock product:', restockData);
                  console.log('Product found in catalog:', productToRestock);
                  
                  if (!productToRestock) {
                    // Show a more user-friendly error message
                    const errorMessage = restockData.name 
                      ? `Product "${restockData.name}" could not be restocked` 
                      : 'Product not found in local catalog';
                    console.error(errorMessage, restockData.productId);
                    throw new Error(errorMessage);
                  }
                  
                  // Call API to restock - use both ID formats if available
                  const idToUse = productToRestock._id || productToRestock.id;
                  await restockProduct(idToUse, restockData.quantity);
                  
                  // Update product catalog with new stock quantity
                  const updatedProduct = {
                    ...productToRestock,
                    inventory: {
                      ...productToRestock.inventory,
                      stockQuantity: (productToRestock.inventory.stockQuantity || 0) + restockData.quantity,
                      lastRestocked: new Date().toISOString(),
                      inStock: true // Automatically mark as in stock when restocked
                    }
                  };
                  
                  // Update state
                  const category = productToRestock.category;
                  const updatedCatalog = {...productCatalog};
                  
                  updatedCatalog[category] = updatedCatalog[category].map(
                    p => p.id === updatedProduct.id ? updatedProduct : p
                  );
                  
                  setProductCatalog(updatedCatalog);
                  saveProducts(updatedCatalog);
                  
                  // Remove from low stock items if no longer low stock
                  if (updatedProduct.inventory.stockQuantity > updatedProduct.inventory.lowStockThreshold) {
                    setLowStockItems(prev => prev.filter(p => p.id !== updatedProduct.id));
                  }
                  
                  // Close modal
                  const modal = document.getElementById('restock-modal');
                  if (modal) {
                    modal.style.display = 'none';
                  }
                  
                  // Show success message
                  showSuccess(`Successfully restocked ${productToRestock.name} with ${restockData.quantity} units.`);
                } catch (error) {
                  console.error('Failed to restock product:', error);
                  showError(`Failed to restock product: ${error.message || 'Please try again.'}`);
                } finally {
                  setIsLoading(false);
                }
              }}>
                <div className="form-group">
                  <label htmlFor="restock-quantity">Quantity to Add</label>
                  <input 
                    type="number" 
                    id="restock-quantity" 
                    value={restockData.quantity}
                    onChange={(e) => setRestockData({
                      ...restockData,
                      quantity: parseInt(e.target.value) || 0
                    })}
                    min="1"
                    required
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={() => {
                      const modal = document.getElementById('restock-modal');
                      if (modal) {
                        modal.style.display = 'none';
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="save-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Restock Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'products' && (
        <div className="products-management">
          <div className="category-selector">
            <h2>Product Catalog Management</h2>
            <div className="category-buttons">
              <button 
                className={`category-btn ${selectedCategory === 'smoothies' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('smoothies')}
              >
                Smoothies
              </button>
              <button 
                className={`category-btn ${selectedCategory === 'juices' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('juices')}
              >
                Juices
              </button>
              <button 
                className={`category-btn ${selectedCategory === 'proteinBalls' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('proteinBalls')}
              >
                Protein Balls
              </button>
            </div>
          </div>

          <div className="product-list-container">
            <div className="product-list-header">
              <h3>{selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}</h3>
              <button 
                className="add-product-btn"
                onClick={handleAddProduct}
              >
                Add New Product
              </button>
            </div>
            
            <div className="products-table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productCatalog && productCatalog[selectedCategory] ? 
                    productCatalog[selectedCategory].map(product => (
                      <tr key={product.id} className={
                        product.inventory && !product.inventory.inStock ? 'out-of-stock' : 
                        product.inventory && product.inventory.stockQuantity <= product.inventory.lowStockThreshold ? 'low-stock' : ''
                      }>
                        <td>{product.id}</td>
                        <td>{product.name}</td>
                        <td className="description-cell">{product.description}</td>
                        <td>${product.price.toFixed(2)}</td>
                        <td className="stock-cell">
                          {product.inventory ? (
                            product.inventory.inStock ? (
                            <span className={product.inventory.stockQuantity <= product.inventory.lowStockThreshold ? 'low-stock-badge' : 'in-stock-badge'}>
                              {product.inventory.stockQuantity}
                            </span>
                          ) : (
                            <span className="out-of-stock-badge">Out of Stock</span>
                          )
                        ) : (
                          <span className="no-inventory-badge">No Data</span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditProduct(product)}
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          Delete
                        </button>
                        {product.inventory && (
                          <button 
                            className="restock-btn small"
                            onClick={() => {
                              // Store both ID formats if available
                              setRestockData({
                                productId: product._id || product.id,
                                quantity: 10,
                                name: product.name // Store name for better error messages
                              });
                              const modal = document.getElementById('restock-modal');
                              if (modal) {
                                modal.style.display = 'flex';
                              }
                            }}
                          >
                            Restock
                          </button>
                        )}
                      </td>
                    </tr>
                  )) : <tr><td colSpan="6">No products available</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {(editingProduct !== null || formData.id !== '' || formData.name !== undefined) ? (
            <div className="product-form-container">
              <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
              <div className="required-fields-note">Fields marked with <span className="required-marker">*</span> are required</div>
              <form onSubmit={isLoading ? (e) => e.preventDefault() : handleSaveProduct} className="product-form" encType="multipart/form-data">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="required-field">Product Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="category" className="required-field">Category</label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="smoothies">Smoothies</option>
                      <option value="juices">Juices</option>
                      <option value="proteinBalls">Protein Balls</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="price" className="required-field">Price ($)</label>
                    <input 
                      type="number" 
                      id="price" 
                      name="price" 
                      value={formData.price}
                      onChange={handleFormChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="description" className="required-field">Description</label>
                  <textarea 
                    id="description" 
                    name="description" 
                    value={formData.description}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="ingredients" className="required-field">Ingredients (comma separated)</label>
                  <input 
                    type="text" 
                    id="ingredients" 
                    name="ingredients" 
                    value={formData.ingredients.join(', ')}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                
                <div className="form-row nutrition-facts">
                  <h4>Nutrition Facts</h4>
                  <div className="form-group">
                    <label htmlFor="nutritionFacts.calories" className="required-field">Calories</label>
                    <input 
                      type="number" 
                      id="nutritionFacts.calories" 
                      name="nutritionFacts.calories" 
                      value={formData.nutritionFacts.calories}
                      onChange={handleFormChange}
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="nutritionFacts.protein" className="required-field">Protein (g)</label>
                    <input 
                      type="number" 
                      id="nutritionFacts.protein" 
                      name="nutritionFacts.protein" 
                      value={formData.nutritionFacts.protein}
                      onChange={handleFormChange}
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="nutritionFacts.fat" className="required-field">Fat (g)</label>
                    <input 
                      type="number" 
                      id="nutritionFacts.fat" 
                      name="nutritionFacts.fat" 
                      value={formData.nutritionFacts.fat}
                      onChange={handleFormChange}
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="nutritionFacts.carbs" className="required-field">Carbs (g)</label>
                    <input 
                      type="number" 
                      id="nutritionFacts.carbs" 
                      name="nutritionFacts.carbs" 
                      value={formData.nutritionFacts.carbs}
                      onChange={handleFormChange}
                      min="0"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="image-file-input" className="required-field">Product Image</label>
                  <div className="image-upload-container">
                    {/* Show image preview - prioritize local preview for newly selected files */}
                    {(imagePreview || formData.image) && (
                      <div className="image-preview">
                        {imagePreview ? (
                          <img src={imagePreview} alt="Product preview" />
                        ) : (
                          <ProductImage product={formData} alt="Product preview" />
                        )}
                      </div>
                    )}
                    <input 
                      type="file" 
                      id="image-file-input" 
                      name="imageFile" 
                      onChange={handleFormChange}
                      accept="image/*"
                      className="image-file-input"
                      disabled={isUploadingImage || isLoading}
                      required={!formData.image} // Only required if no existing image
                    />
                    <div className="file-input-help">
                      {isUploadingImage 
                        ? 'Uploading image...' 
                        : imageFile 
                          ? `Selected: ${imageFile.name}` 
                          : 'No file selected'}
                    </div>
                    <span className="form-help">
                      {isUploadingImage 
                        ? 'Please wait, your image is being processed...' 
                        : 'Upload a product image (jpg, png, etc.)'}
                    </span>
                  </div>
                </div>
                
                <div className="form-row inventory-section">
                  <h4>Inventory Management</h4>
                  
                  <div className="form-group">
                    <label htmlFor="inventory.sku">SKU (Stock Keeping Unit)</label>
                    <input 
                      type="text" 
                      id="inventory.sku" 
                      name="inventory.sku" 
                      value={formData.inventory?.sku || ''}
                      onChange={handleFormChange}
                      placeholder="SKU-123456"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="inventory.stockQuantity" className="required-field">Stock Quantity</label>
                    <input 
                      type="number" 
                      id="inventory.stockQuantity" 
                      name="inventory.stockQuantity" 
                      value={formData.inventory?.stockQuantity || 0}
                      onChange={handleFormChange}
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="inventory.lowStockThreshold" className="required-field">Low Stock Threshold</label>
                    <input 
                      type="number" 
                      id="inventory.lowStockThreshold" 
                      name="inventory.lowStockThreshold" 
                      value={formData.inventory?.lowStockThreshold || 5}
                      onChange={handleFormChange}
                      min="1"
                      required
                    />
                    <span className="form-help">Alert will show when stock is below this number</span>
                  </div>
                  
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input 
                        type="checkbox" 
                        name="inventory.inStock" 
                        checked={formData.inventory?.inStock !== false}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            inventory: {
                              ...formData.inventory,
                              inStock: e.target.checked
                            }
                          });
                        }}
                      />
                      <span>Available for Sale</span>
                    </label>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={handleCancelEdit}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="save-btn"
                    disabled={isLoading}
                  >
                    {isLoading 
                      ? 'Saving...' 
                      : (editingProduct ? 'Update Product' : 'Add Product')
                    }
                  </button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default Admin;